import { Checkbox } from '@atlaskit/checkbox';
import { EditComponentProps } from '@databases/fields/edit/types';
import { flaggingCallback, useFlaggingCallback } from '@databases/flags';
import FieldDecorator from '@databases/form/FieldDecorator';
import { messages } from '@databases/intl/messages';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { UserReference } from '@databases/types/confluence';
import {
  DatabaseFieldType,
  RawDatabaseValue,
  USER_VALUE_CURRENT,
} from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { isNonEmptyUserValue } from '@databases/utils/database/field-types/user';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { loadUserById } from '@databases/utils/redux/users/actions';
import { selectUserByIdOptional } from '@databases/utils/redux/users/selectors';
import { generateId } from '@databases/utils/utils';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import type { EditableListProps } from '../../EditableList';
import { EditableListWithPlaceholder } from '../../EditableListWithPlaceholder';
import UserPicker from '../../UserPicker';

type Value = Exclude<RawDatabaseValue<DatabaseFieldType.USER>, string>[0];

const getId = (value: Value): string => value._id;

const getNewValue = (): Value => ({ _id: generateId() });

const UserValueEdit: FunctionComponent<EditComponentProps<DatabaseFieldType.USER>> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  isDefaultValue,
}) => {
  const currentUserId = defaultStoreUtils.useSelector(selectCurrentUserId);
  const currentUser = defaultStoreUtils.useSelector((state) =>
    selectUserByIdOptional(state, { accountId: currentUserId })
  );

  const defaultId = useMemo(() => generateId(), []);

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useFlaggingCallback(async () => {
      if (value === USER_VALUE_CURRENT && currentUserId) {
        await dispatch(loadUserById(currentUserId));
      }
    }, [currentUserId, dispatch, value])
  );

  const { formatMessage } = useIntl();

  const handleToggleCurrent = useFlaggingCallback(async () => {
    if (value === USER_VALUE_CURRENT) {
      const userValues = currentUser ? [{ ...currentUser, _id: defaultId }] : [];
      await onChange(userValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.SET_TO,
          value: userValues,
        },
      });
    } else {
      await onChange(USER_VALUE_CURRENT, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.SET_TO,
          value: USER_VALUE_CURRENT,
        },
      });
    }
  }, [defaultId, currentUser, onChange, value]);

  const resolvedValue = defaultStoreUtils.useEqualSelector((state): Value[] => {
    let result: Value[];
    if (value === USER_VALUE_CURRENT) {
      result = currentUser ? [{ ...currentUser, _id: defaultId }] : [];
    } else {
      // Use updated display names if a user was renamed
      result = value.map((v) => {
        const user = isNonEmptyUserValue(v)
          ? selectUserByIdOptional(state, { accountId: v.accountId })
          : undefined;
        return user ? { ...v, displayName: user.displayName } : v;
      });
    }
    return result;
  });

  const handleSingleChange = useFlaggingCallback(
    async (val: UserReference[]) => {
      const userValues = [];
      if (val.length > 0) {
        userValues.push({ ...val[0], _id: resolvedValue[0]?._id ?? generateId() });
      }

      await onChange(userValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.SET_TO,
          value: userValues,
        },
      });
    },
    [onChange, resolvedValue]
  );

  const onItemChange = useFlaggingCallback(
    async (val: Value, newValueIndex: number, _: null, allValues: Value[]) => {
      await onChange(allValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.EDIT,
          value: val,
          index: newValueIndex,
        },
      });
    },
    [onChange]
  );

  const onItemAdd = useFlaggingCallback(
    async (val: Value, allValues: Value[]) => {
      await onChange(allValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: val,
        },
      });
    },
    [onChange]
  );

  const onItemDelete = useFlaggingCallback(
    async (_: Value, removedIndex: number, allValues: Value[]) => {
      await onChange(allValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: removedIndex,
        },
      });
    },
    [onChange]
  );

  const onItemReorder = useFlaggingCallback(
    async (_: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      await onChange(allValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.REORDER,
          currentIndex,
          newIndex,
        },
      });
    },
    [onChange]
  );

  const isMulti =
    (field.settings.multi || resolvedValue.length > 1) &&
    !(isDefaultValue && value === USER_VALUE_CURRENT);

  const renderField: EditableListProps<Value, null>['renderField'] = useCallback(
    ({ value, autoFocus, onChange }) => (
      <UserPicker
        value={isNonEmptyUserValue(value) ? [value] : []}
        onChange={flaggingCallback((val) => {
          onChange({ ...(val[0] ?? {}), _id: value._id }, null);
        })}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        isSeamless
        isMentionPreview
      />
    ),
    [onBlur, onFocus]
  );

  let picker;
  if (isMulti) {
    picker = (
      <EditableListWithPlaceholder<Value, null>
        getId={getId}
        values={resolvedValue}
        onItemChange={onItemChange}
        onAddNewItem={onItemAdd}
        onRemoveItem={onItemDelete}
        onReorder={onItemReorder}
        getNewValue={getNewValue}
        renderField={renderField}
        autoFocus={value.length === 0 ? autoFocus : undefined}
        addLabel={formatMessage(messages['field-settings.type-user-add-user-label-text'])}
        noAdd={!field.settings.multi}
      />
    );
  } else {
    const userPickerValue =
      resolvedValue[0] && isNonEmptyUserValue(resolvedValue[0]) ? [resolvedValue[0]] : [];
    picker = (
      <UserPicker
        value={userPickerValue}
        onChange={handleSingleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        isClearable
        isDisabled={isDefaultValue && value === USER_VALUE_CURRENT}
        isMentionPreview
      />
    );
  }

  if (isDefaultValue) {
    return (
      <>
        {picker}
        <FieldDecorator isCompact>
          <Checkbox
            isChecked={value === USER_VALUE_CURRENT}
            onChange={handleToggleCurrent}
            onFocus={onFocus}
            onBlur={onBlur}
            label={formatMessage(
              messages['field-settings.type-user-set-current-user-default-text']
            )}
          />
        </FieldDecorator>
      </>
    );
  } else {
    return picker;
  }
};

export default UserValueEdit;
